// NewDatabaseRowModal.tsx
import React from 'react';
import './NewDatabaseRowModal.css';

interface NewDatabaseRowModalProps {
    isOpen: boolean;
    onClose: () => void;
    rowData: any | null;
}

interface DetailField {
    label: string;
    key: string;
    formatter?: (value: any) => string;
    className?: string;
}

interface DetailSection {
    title?: string;
    fields: DetailField[];
    className?: string;
}

const NewDatabaseRowModal: React.FC<NewDatabaseRowModalProps> = ({ 
    isOpen, 
    onClose, 
    rowData 
}) => {
    // Always render the component but control visibility via CSS
    // if (!isOpen || !rowData) return null;

    // Helper function to format numbers consistently
    const formatNumber = (value: any): string => {
        const numValue = Number(value) || 0;
        return numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Helper function to format currency
    const formatCurrency = (value: any): string => {
        return `$${formatNumber(value)}`;
    };

    // Helper function to format status
    const formatStatus = (value: any): string => {
        return value || '';
    };

    // Configuration for detail sections
    const detailSections: DetailSection[] = [
        {
            title: "Volume & Quality",
            fields: [
                { label: "Gross Barrels", key: "gross_barrels", formatter: formatNumber },
                { label: "BS&W", key: "bsw", formatter: formatNumber },
                { label: "Net Barrels", key: "net_barrels", formatter: formatNumber },
                { label: "Observed Temp.", key: "observed_temperature", formatter: formatNumber },
                { label: "Observed Gravity", key: "observed_gravity", formatter: formatNumber },
                { label: "Corrected Gravity", key: "corrected_gravity", formatter: formatNumber },
            ]
        },
        {
            title: "Location & Status",
            fields: [
                { label: "Producer", key: "producer" },
                { label: "Lease", key: "lease" },
                { label: "Carrier", key: "carrier" },
                { label: "Status", key: "status", formatter: formatStatus, className: "status-field" },
                { label: "Station Owner", key: "station_owner" },
                { label: "Station", key: "destination" },
                { label: "Market", key: "market" },
            ]
        },
        {
            title: "Purchase Details",
            fields: [
                { label: "Total Trucking", key: "total_trucking", formatter: formatCurrency },
                { label: "Purchase Deduct", key: "purchase_deduct", formatter: formatCurrency },
                { label: "Purchase BS&W Deduct", key: "purchase_bsw_deduct", formatter: formatCurrency },
                { label: "Additional Deduct", key: "additional_deduct", formatter: formatCurrency },
                { label: "Purchase Price", key: "purchase_price", formatter: formatCurrency },
            ]
        },
        {
            title: "Sale Details",
            fields: [
                { label: "Sale Deduct", key: "sale_deduct", formatter: formatCurrency },
                { label: "Sale BS&W Deduct", key: "sale_bsw_deduct", formatter: formatCurrency },
                { label: "Sale Additional Deduct", key: "sale_location_deduct", formatter: formatCurrency },
                { label: "Station Charge", key: "station_charge", formatter: formatCurrency },
                { label: "Sale Price", key: "sale_price", formatter: formatCurrency },
            ]
        },
        {
            title: "Financial Summary",
            fields: [
                { label: "Total Purchase", key: "total_purchase", formatter: formatCurrency },
                { label: "Total Sale", key: "total_sale", formatter: formatCurrency },
                { label: "Net Profit", key: "net_profit", formatter: formatCurrency },
            ]
        }
    ];

    // Component to render individual field
    const DetailField = ({ field }: { field: DetailField }) => {
        const value = rowData[field.key];
        const formattedValue = field.formatter ? field.formatter(value) : (value || '-');
        
        // Special handling for status field
        const getStatusClassName = () => {
            if (field.key === 'status' && value) {
                const statusLower = value.toString().toLowerCase();
                if (statusLower === 'rejected') {
                    return 'newdb-modal-status-rejected';
                } else if (statusLower === 'completed') {
                    return 'newdb-modal-status-completed';
                }
            }
            return '';
        };

        return (
            <div className="newdb-modal-row-item">
                <div className="newdb-modal-row-label">{field.label}</div>
                <div className={`newdb-modal-row-value ${getStatusClassName()}`}>
                    {formattedValue}
                </div>
            </div>
        );
    };

    // Component to render section
    const DetailSection = ({ section }: { section: DetailSection }) => (
        <div className="newdb-modal-section">
            {section.title && <h4 className="newdb-modal-section-title">{section.title}</h4>}
            <div className="newdb-modal-section-content">
                {section.fields.map((field) => (
                    <DetailField key={field.key} field={field} />
                ))}
            </div>
        </div>
    );

    return (
        <div className={`newdb-modal-container ${isOpen ? 'open' : ''}`}>
            <div className="newdb-modal-header">
                <h3>RID: {rowData?.rid || rowData?.id || 'N/A'}</h3>
                <button onClick={onClose} className="newdb-modal-close-button">
                    &times;
                </button>
            </div>
            
            <div className="newdb-modal-content">
                {rowData && detailSections.map((section, index) => (
                    <DetailSection key={index} section={section} />
                ))}
            </div>
        </div>
    );
};

export default NewDatabaseRowModal;
